import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { objects, strings, validators } from 'investira.sdk';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from 'investira.react.components';
import utils from '../../../utils';
import { SETORES } from '../../../enums';
import SearchEmpresasItem from './SearchEmpresasItem';

const wPageSize = 10;

/** Formata o texto para o contrato de pesquisa full text da API. */
function pvFormatPesquisa(pPesquisa) {
  return String(pPesquisa || '')
    .replace(/[./-]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(pTermo => `+${pTermo}*`)
    .join('');
}

/** Resolve a rota que receberá todos os resultados da pesquisa atual. */
function pvGetResultsPath(pPathname) {
  const xPathname = pPathname.replace(/\/page\/\d+$/, '');

  return xPathname === '/empresas' ? '/empresas/busca' : xPathname;
}

/**
 * Pesquisa exclusivamente empresas e mantém a consulta sincronizada com a URL.
 * A função de leitura recebida é a mesma utilizada pela listagem e pode impor o
 * setor da rota antes de chamar `services.pesquisas.pessoa`.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {Function} pProps.dataRead Consulta utilizada para carregar as sugestões.
 * @param {Object} [pProps.dataReadParams] Filtros aplicados à consulta de sugestões.
 * @param {boolean} [pProps.pageIsLoading=false] Indica se a listagem está carregando.
 * @param {string} [pProps.setorId] Setor usado para contextualizar a pesquisa.
 * @returns {JSX.Element} Campo de pesquisa e diálogo com sugestões de empresas.
 */
const SearchEmpresas = memo(pProps => {
  const { dataRead, dataReadParams = {}, pageIsLoading = false, setorId = null } = pProps;
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const xQuery = strings.querystringToObject(xLocation.search) || {};
  const xSearchValue = xQuery.pesquisa || '';
  const xSetor = setorId ? SETORES[setorId] : null;
  const xPlaceholder = xSetor
    ? `Pesquisar por empresas do setor ${xSetor.setor.toLocaleLowerCase('pt-BR')}`
    : 'Pesquisar por empresas';
  const xBaseRequestContextKey = JSON.stringify({ setorId, params: dataReadParams });
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [pesquisa, setPesquisa] = useState(xSearchValue);
  const [data, setData] = useState([]);
  const [message, setMessage] = useState(null);
  const [ignoredFiltersContext, setIgnoredFiltersContext] = useState(null);
  const [hasEmptyResult, setHasEmptyResult] = useState(false);
  const xIgnoreFilters = ignoredFiltersContext === xBaseRequestContextKey;
  const xEffectiveDataReadParams = xIgnoreFilters ? {} : dataReadParams;
  const xRequestContextKey = JSON.stringify({
    context: xBaseRequestContextKey,
    ignoreFilters: xIgnoreFilters
  });
  const xCanIgnoreAppliedFilters = Boolean(setorId) && Object.keys(dataReadParams).length > 0;
  const inputRef = useRef();
  const placeholderRef = useRef();
  const requestIdRef = useRef(0);
  const requestContextRef = useRef(xRequestContextKey);
  const isUpMd = useMediaQuery(pTheme => pTheme.breakpoints.up('md'));
  const xRequest = useMemo(() => utils.helpers.debounce(dataRead, 1000), [dataRead]);

  /** Fecha o diálogo e restaura o valor confirmado na URL. */
  function pvClose() {
    setOpen(false);
    setPesquisa(xSearchValue);
    setData([]);
    setMessage(null);
    setHasEmptyResult(false);
    setIgnoredFiltersContext(null);
    placeholderRef.current?.blur();
    inputRef.current?.blur();
  }

  /** Atualiza a URL preservando todos os filtros compatíveis. */
  function pvSearchAll() {
    const xResultsPath = pvGetResultsPath(xLocation.pathname);
    const xNextQuery = { ...xQuery };

    // No modo ampliado, remove somente os filtros opcionais aplicados pelo sidebar.
    if (xIgnoreFilters) {
      Object.keys(dataReadParams).forEach(pParam => delete xNextQuery[pParam]);
    }

    xNextQuery.pesquisa = pesquisa.trim();
    const xSearch = objects.objectToQueryString(xNextQuery);

    pvClose();
    xNavigate(`${xResultsPath}${xSearch}`);
  }

  /** Repete a consulta mantendo o setor e ignorando os filtros opcionais. */
  function pvSearchWithoutFilters() {
    setIgnoredFiltersContext(xBaseRequestContextKey);
  }

  /** Remove somente a pesquisa textual ativa. */
  function pvClearSearch(pEvent) {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    const xQueryWithoutSearch = { ...xQuery };
    const xResultsPath = pvGetResultsPath(xLocation.pathname);

    delete xQueryWithoutSearch.pesquisa;
    setPesquisa('');
    setData([]);
    xNavigate(`${xResultsPath}${objects.objectToQueryString(xQueryWithoutSearch)}`);
  }

  /** Abre o detalhe da empresa selecionada. */
  function pvGoToEmpresa(pEvent, pPessoaId) {
    pEvent.preventDefault();
    pEvent.stopPropagation();
    pvClose();
    xNavigate(`/empresas/${pPessoaId}`);
  }

  // A URL restaura o texto ao navegar pelo histórico ou trocar de página.
  useEffect(() => {
    setPesquisa(xSearchValue);
  }, [xSearchValue]);

  // Ao abrir, transfere o foco do campo externo para a busca efetiva do diálogo.
  useEffect(() => {
    if (open) {
      placeholderRef.current?.blur();
      inputRef.current?.focus();
    }
  }, [open]);

  // Uma mudança no contexto aplicado fecha a busca e restaura seu modo padrão.
  useEffect(() => {
    requestIdRef.current += 1;
    setOpen(false);
    setLoading(false);
    setData([]);
    setMessage(null);
    setHasEmptyResult(false);
    setIgnoredFiltersContext(null);
  }, [xBaseRequestContextKey]);

  // Sugestões e respostas pendentes não atravessam o modo efetivo da consulta.
  useEffect(() => {
    requestIdRef.current += 1;
    requestContextRef.current = xRequestContextKey;
    setLoading(false);
    setData([]);
    setMessage(null);
    setHasEmptyResult(false);
  }, [xRequestContextKey]);

  // A consulta só acontece com o diálogo aberto e pelo menos dois caracteres.
  useEffect(() => {
    const xRequestId = ++requestIdRef.current;

    if (!open || pesquisa.trim().length < 2 || pageIsLoading) {
      setLoading(false);
      setData([]);
      setMessage(null);
      return;
    }

    setLoading(true);
    setData([]);
    setMessage(null);
    setHasEmptyResult(false);
    const xRequestContext = xRequestContextKey;

    xRequest(
      {
        ...xEffectiveDataReadParams,
        pesquisa: pvFormatPesquisa(pesquisa),
        size: wPageSize,
        sort: 'relevancia DESC, __mr__ DESC, pessoa_fantasia ASC'
      },
      pResponse => {
        // Descarta respostas iniciadas antes de uma troca de setor.
        if (requestIdRef.current !== xRequestId || requestContextRef.current !== xRequestContext) {
          return;
        }

        const xData = Object.values(pResponse.data || {});

        setData(xData);
        setHasEmptyResult(!xData.length);
        setMessage(
          xData.length
            ? null
            : xCanIgnoreAppliedFilters && !xIgnoreFilters
            ? 'Nenhum resultado encontrado com os filtros aplicados.'
            : setorId
            ? 'Nenhum resultado encontrado neste setor.'
            : 'Nenhuma empresa encontrada'
        );
      },
      () => {
        if (requestIdRef.current === xRequestId && requestContextRef.current === xRequestContext) {
          setHasEmptyResult(false);
          setMessage('Ocorreu um erro durante a busca');
        }
      },
      () => {
        if (requestIdRef.current === xRequestId && requestContextRef.current === xRequestContext) {
          setLoading(false);
        }
      }
    );
  }, [open, pesquisa, pageIsLoading, xRequest, xRequestContextKey]);

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: {
          sx: {
            pt: 3,
            pb: 2,
            px: 2
          }
        },
        maxWidth: 'lg'
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: { margin: 0, backgroundColor: 'background.dark' }
        }
      };

  return (
    <Stack id="search-empresas" sx={{ position: 'relative', width: '100%', zIndex: 999 }}>
      {pageIsLoading ? (
        <Skeleton variant="rounded" width="100%" height={56} />
      ) : (
        <TextField
          id="pesquisa-empresas"
          inputRef={placeholderRef}
          value={pesquisa}
          onFocus={() => setOpen(true)}
          variant="outlined"
          placeholder={xPlaceholder}
          size="normal"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Icon iconName="find" size={21} color="primary" />
              </InputAdornment>
            ),
            endAdornment: !validators.isEmpty(xSearchValue) && (
              <InputAdornment position="end">
                <IconButton aria-label="Limpar pesquisa" color="primary" onClick={pvClearSearch}>
                  <Icon iconName="cancel" size={18} color="primary" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}

      <Dialog
        {...xDialogProps}
        open={open}
        onClose={pvClose}
        disableRestoreFocus
        keepMounted
        fullWidth>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Stack>
              <IconButton aria-label="Fechar pesquisa" onClick={pvClose} color="primary">
                <Icon iconName="arrow_left" size={21} />
              </IconButton>
            </Stack>
            <Stack flexGrow={1}>
              <TextField
                id="pesquisa-empresas-dialog"
                inputRef={inputRef}
                value={pesquisa}
                onChange={pEvent => setPesquisa(pEvent.currentTarget.value)}
                sx={{ border: 'none' }}
                variant="outlined"
                size={isUpMd ? 'normal' : 'small'}
                placeholder={xPlaceholder}
                fullWidth
                disabled={pageIsLoading}
              />
            </Stack>
          </Stack>
          <Divider />

          {!isLoading && data.length > 0 && (
            <>
              <Stack id="pesquisa-empresas-list" component="ul" sx={{ px: 0 }}>
                {data.map(pPessoa => (
                  <SearchEmpresasItem
                    key={pPessoa.pessoa_id}
                    pessoa={pPessoa}
                    showSetor={Boolean(setorId)}
                    onClick={pEvent => pvGoToEmpresa(pEvent, pPessoa.pessoa_id)}
                  />
                ))}
              </Stack>
              <Stack sx={{ px: 2, py: 2, alignItems: 'center' }}>
                <Button variant="contained" onClick={pvSearchAll}>
                  Visualizar todos
                </Button>
              </Stack>
            </>
          )}

          {isLoading && (
            <Stack justifyContent="center" alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {!isLoading && message && (
            <Stack justifyContent="center" alignItems="center" spacing={2} sx={{ py: 2 }}>
              <Typography variant="body2" color="textPrimary">
                {message}
              </Typography>
              {hasEmptyResult && xCanIgnoreAppliedFilters && !xIgnoreFilters && (
                <Button variant="outlined" onClick={pvSearchWithoutFilters}>
                  Pesquisar ignorando os filtros
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
});

SearchEmpresas.propTypes = {
  dataRead: PropTypes.func.isRequired,
  dataReadParams: PropTypes.object,
  pageIsLoading: PropTypes.bool,
  setorId: PropTypes.string
};

SearchEmpresas.displayName = 'SearchEmpresas';

export default SearchEmpresas;
